import {
  connectorsBulkAddContactsCreateMutation,
  connectorsListListOptions,
  connectorsOptionsRetrieveOptions
} from '@crm/api'
import { FieldsProps } from '@crm/types'

import { onChangeFormatForAPI } from '@crm/utils'
import { useQueryParams } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Form, Modal, Select } from 'antd'
import { useState } from 'react'

import type { ModalProps } from 'antd'

const formName = 'send-client'

interface SendClientProps extends ModalProps {
  leads?: number[]
  dataFields?: FieldsProps
  onClose: () => void
  onSubmit: () => void
}

export const SendClientForm = ({
  leads,
  dataFields,
  onClose,
  ...props
}: SendClientProps) => {
  const { t } = useLocales()
  const params = useQueryParams()
  const [form] = Form.useForm()

  const [openOptions, setOpenOptions] = useState(false)
  const [canSubmit, setCanSubmit] = useState(false)

  const selectedConnector = Form.useWatch('connector', form)

  const { data: connectors, isLoading: isLoadingConnector } = useQuery(
    connectorsListListOptions()
  )

  const { data: options } = useQuery(connectorsOptionsRetrieveOptions())

  const handleValuesChange = (field: {
    connector?: string
    option?: string
  }) => {
    if (field?.connector) {
      setOpenOptions(true)
    }
    if (field?.option) {
      setCanSubmit(true)
    }
  }

  const { mutate: sendClientMutation } = useMutation({
    ...connectorsBulkAddContactsCreateMutation(),
    onError: (error) => showApiErrors(error, form),
    onSuccess: () => {
      onClose()
    }
  })

  const handleSubmit = async () => {
    const values = await form.validateFields()

    sendClientMutation({
      ...values,
      leads,
      query: onChangeFormatForAPI(params, dataFields)
    })
  }

  return (
    <Modal
      title={t('activities.send')}
      cancelText={t('buttons.cancel')}
      onCancel={onClose}
      okText={t('buttons.save')}
      okButtonProps={{
        htmlType: 'submit',
        disabled: !canSubmit,
        form: formName
      }}
      {...props}
    >
      <Form
        form={form}
        name={formName}
        onValuesChange={handleValuesChange}
        onFinish={handleSubmit}
        layout="vertical"
      >
        <Form.Item name="connector" label={t('lead.connector')}>
          <Select
            loading={isLoadingConnector}
            placeholder={t('main.select')}
            options={connectors?.map((item) => ({
              value: item,
              label: item
            }))}
          />
        </Form.Item>

        {openOptions && (
          <Form.Item name="option" label={t('lead.option')}>
            <Select
              options={options?.map((item) => ({
                value: item,
                label: item
              }))}
              placeholder={t('task.reason')}
              disabled={!selectedConnector}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}
